import { useQueryClient } from "@tanstack/react-query";
import { delay, uniqBy } from "lodash";
import PropTypes from "prop-types";
import { useState } from "react";
import { useParams } from "react-router-dom";
import LoadingOverlay from "../../../../components/LoadingOverlay";
import RenderWhen from "../../../../components/RenderWhen";
import Title from "../../../../components/Title";
import { AnalysisStatus } from "../../../../lib/enums";
import { INITIAL_ANALYSIS_SELECTIONS } from "../reanalyze/reanalyze-popup/consts";
import { ReanalyzeButton } from "../topheader/components";
import RLCloudSandboxError from "./RLCloudSandboxError";
import RLCloudSandboxTabs from "./RLCloudSandboxTabs";
import RLCloudSandboxToolbar from "./RLCloudSandboxToolbar";
import RlCloudSandboxSummary from "./RlCloudSandboxSummary";
import { RlCloudSandboxReportDownloadMenu } from "./download";
import { RlCloudSandboxHistory } from "./historytable";
import { QUOTA_EXCEEDED_MSG } from "./historytable/HistoryTitle";
import { useGetMergedRlcsReport } from "./hooks/useGetMergedRlcsReport";
import { useGetSingleRlcsReport } from "./hooks/useGetSingleRlcsReport";
import { useRlcsAnalysisStatus } from "./hooks/useRlcsAnalysisStatus";
import ViewScreenshotsButton from "./screenshots/ViewScreenshotsButton";
import { getLatestFinishedAnalysis } from "./utils";
import InteractiveAnalysisLink from "../interactiveanalysis/components/interactive-analysis-link/InteractiveAnalysisLink";
import useScrollTo from "../../../../hooks/useScrollTo";

const DEFAULT_REFRESH_RATE = 15 * 1000;

const getTooltipMessageForDisplayStatus = displayStatus =>
  displayStatus === QUOTA_EXCEEDED_MSG
    ? "Daily/Monthly quota has been exceeded."
    : undefined;

const RlCloudSandbox = ({ filename }) => {
  useScrollTo();
  const { url, hash, sourceId, section } = useParams();
  const isCloudDetailsPage = !sourceId;
  const isNetworkReport = Boolean(url);
  const [activeFilter, setActiveFilter] = useState("all");
  const isMergedSelected = !activeFilter || activeFilter === "all";
  const singleReportId = isMergedSelected ? "latest" : activeFilter;

  const queryClient = useQueryClient();

  const {
    mergedReportData,
    isLoadingMerged,
    refetchMergedReport,
    isSuccess: isMergedSuccess,
    error: mergedReportError
  } = useGetMergedRlcsReport(hash, isNetworkReport);
  const {
    data: singleReportData,
    isInitialLoading: singleLoading,
    isFetched: isSingleReportFetched
  } = useGetSingleRlcsReport(hash, singleReportId, isNetworkReport, {
    select: ({ data }) => ({
      ...(isMergedSelected ? {} : data), // enrich merged report with behavioral data only
      behavioral: data.behavioral,
      process_tree: uniqBy(data.process_tree, process => process.process_id)
    }),
    enabled: isMergedSuccess
  });

  const { analysisStatusData, refetchAnalysisStatus, analysisStatusQueryKey } =
    useRlcsAnalysisStatus({
      hash,
      refetchMergedReportFn: refetchMergedReport,
      pollDisabled: isCloudDetailsPage
    });

  const handleHistoryDropdownChange = value => {
    if (!value) {
      refetchMergedReport();
    }
    setActiveFilter(value);
  };

  const reportData = isMergedSelected
    ? { ...mergedReportData, ...singleReportData } // combine merged data & latest behavioral data
    : singleReportData;

  const {
    general_info: generalInfo,
    history_analysis: historyAnalysis,
    is_stored_report: isStoredReport,
    private_sample: isSamplePrivate
  } = mergedReportData || {};

  const { automated, interactive } = analysisStatusData ?? {};
  const { display_status: automatedStatus } = automated ?? {};
  const { display_status: interactiveStatus } = interactive ?? {};

  const isAnalyzing = interactive?.analyzing || automated?.analyzing;

  const errorDisplayStatus =
    automatedStatus ?? interactiveStatus ?? "Loading...";
  const latestFinishedAnalysisId =
    getLatestFinishedAnalysis(historyAnalysis)?.analysis_id ?? "";
  const showScreenshotsThumbNail =
    !isSamplePrivate && (!isStoredReport || isNetworkReport);

  if (isLoadingMerged) {
    return <LoadingOverlay />;
  }

  const tabsContent =
    isLoadingMerged || singleLoading ? (
      <div className={"py-10"}>
        <LoadingOverlay displayInline />
      </div>
    ) : (
      <RLCloudSandboxTabs
        hash={hash}
        sourceId={sourceId}
        activeSection={section}
        activeReport={activeFilter}
        data={reportData}
        isStoredReport={isStoredReport}
        isPrivate={isSamplePrivate}
        isNetworkReport={isNetworkReport}
        historyAnalysis={historyAnalysis}
        isMergedSelected={isMergedSelected}
        isLatestFetched={isSingleReportFetched && singleReportId === "latest"}
      />
    );

  return (
    <>
      <div className="rlbs rl-cloud-sandbox">
        <div className={"d-flex flex-row justify-content-between mb-20"}>
          <div>
            <Title
              Level="h1"
              text="Cloud Sandbox Analysis Summary"
              icon="rl-logo-square"
              contentBottom={
                <RenderWhen condition={isStoredReport}>
                  <div className="bold">
                    <span>
                      Warning! Encountered issues while fetching analysis data
                      from the cloud. Showing locally stored data.
                    </span>
                  </div>
                </RenderWhen>
              }
              className={"mb-0"}
            />
          </div>
          <RenderWhen condition={!mergedReportError && !isCloudDetailsPage}>
            <div className="d-flex gap-10">
              <RenderWhen condition={interactive.analyzing}>
                <InteractiveAnalysisLink
                  hash={hash}
                  uploadId={sourceId}
                  url={url}
                />
              </RenderWhen>
              <ReanalyzeButton
                initialAnalysisSelections={{
                  ...INITIAL_ANALYSIS_SELECTIONS,
                  rl_dynamic_analysis: !interactive.analyzing,
                  rl_dynamic_analysis_interactive: !interactive.analyzing
                }}
                disabled={
                  automatedStatus === QUOTA_EXCEEDED_MSG ||
                  (automated.analyzing && interactive.analyzing)
                }
                tooltipMessage={getTooltipMessageForDisplayStatus(
                  automatedStatus
                )}
                onReanalyze={payload => {
                  const { integrations = [] } = payload ?? {};
                  const automatedSent = integrations.includes(
                    "rl_dynamic_analysis"
                  );
                  const interactiveSent = integrations.includes(
                    "rl_dynamic_analysis_interactive"
                  );
                  queryClient.setQueryData(analysisStatusQueryKey, {
                    automated: {
                      analyzing: automatedSent,
                      analysis_status: automatedSent
                        ? "Fetching analysis data..."
                        : null,
                      display_status: automatedSent
                        ? AnalysisStatus.QUEUED
                        : null
                    },
                    interactive: {
                      analyzing: interactiveSent,
                      analysis_status: interactiveSent
                        ? "Fetching analysis data..."
                        : null,
                      display_status: interactiveSent
                        ? AnalysisStatus.QUEUED
                        : null
                    }
                  });

                  delay(() => refetchMergedReport(), 5 * 1000); // refetch merged report as we show analysis status in history table
                  delay(() => refetchAnalysisStatus(), DEFAULT_REFRESH_RATE); // analysis status takes some time to update
                }}
                shouldRefetchRlcsAnalysisStatus={false}
              />

              <RlCloudSandboxReportDownloadMenu
                analysisId={latestFinishedAnalysisId}
                hash={hash}
                isStoredReport={isStoredReport}
                isPrivate={isSamplePrivate}
                isNetworkReport={isNetworkReport}
              />
            </div>
          </RenderWhen>
        </div>
        {mergedReportError ? (
          <RLCloudSandboxError
            dataError={mergedReportError?.data?.message}
            isAnalyzing={isAnalyzing}
            displayStatus={errorDisplayStatus}
            sampleParams={{ hash, uploadId: sourceId, url }}
          />
        ) : (
          <>
            <RlCloudSandboxSummary
              filename={filename || "--"}
              data={generalInfo}
              screenshotsThumbnailComp={
                <RenderWhen condition={showScreenshotsThumbNail}>
                  <ViewScreenshotsButton
                    hash={hash}
                    historyAnalysis={historyAnalysis}
                    isNetwork={isNetworkReport}
                  />
                </RenderWhen>
              }
            />

            <RlCloudSandboxHistory
              status={analysisStatusData}
              data={historyAnalysis}
              hash={hash}
              isStoredReport={isStoredReport}
              isNetworkReport={isNetworkReport}
              onReportClick={handleHistoryDropdownChange}
            />
            <RLCloudSandboxToolbar
              historyAnalysis={historyAnalysis}
              activeFilter={activeFilter}
              handleHistoryDropdownChange={handleHistoryDropdownChange}
              isStoredReport={isStoredReport}
            />

            {tabsContent}
          </>
        )}
      </div>
    </>
  );
};

RlCloudSandbox.propTypes = {
  filename: PropTypes.string
};

export default RlCloudSandbox;
